import {
  LitElement,
  html,
  TemplateResult,
  PropertyValues,
  css,
} from 'lit';
import {customElement, property, query, state} from 'lit/decorators';
import {ref} from 'lit/directives/ref';
import { mapLegendStyles } from './sb-carto-styles';
import { ILegendLayerInfo, Map, MapLayer, MapLayerKMS } from '../../../shared/map/kelydra-map';
import { SilverBrowserMapLayerLegend } from './sb-carto-layer-legend';
import { classMap } from 'lit/directives/class-map';
import { MapLayerTilesDynamic } from '../../../shared/map/kelydra-map-tiles';
import { scrollbarStyles } from '../../../core/styles';


/**
 *  @customElement 'sb-map-layer-control'
 */
@customElement('sb-map-layer-control')
export class SilverBrowserMapLayerControl extends LitElement {

  static styles = [
    mapLegendStyles,
    scrollbarStyles,
    css`
      .closed .legend-detail {
        display:none;
      }

      .closed .legend-detail.haveFilter {
        display:initial;
      }

    `
  ];

  @property({type:Map})
  map:Map;

  @property()
  layerName:string;

  @property({type:Boolean})
  auto:boolean = true;

  @property({type:Boolean})
  collapsed:boolean;

  @state()
  _filter:string;

  haveFilter:boolean;

  @query('#visibilityControl')
  visibilityControl:HTMLInputElement;

  @query('#opacityControl')
  opacityControl:HTMLInputElement;

  _layer:MapLayer;

  get filter():string {
    return this._filter;
  }

  set filter(value:string) {
    this._filter = value;
    this.haveFilter = !!this._filter;
    this.updateFilter();
  }

  public layersAction(action:'visibility.hide' | 'visibility.show' | 'visibility.reset' | 'tree.close' | 'tree.open') {
    if (action === 'tree.close' && !this.collapsed) {
      this.collapsed = true; 
    } else if (action === 'tree.open' && this.collapsed) {
      this.collapsed = false; 
    }
    this.renderRoot.querySelectorAll('sb-map-layer-legend').forEach(element => {(element as SilverBrowserMapLayerLegend).viewAction(action)});
  }

  public layersUpdate() {
    this.renderRoot.querySelectorAll('sb-map-layer-legend').forEach(element => {(element as SilverBrowserMapLayerLegend).updateLayer()});
  }

  public legendUpdate() {
    this.renderRoot.querySelectorAll('sb-map-layer-legend').forEach(element => {(element as SilverBrowserMapLayerLegend).updateLegend()});
  }



  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
  }


  handleCollapseClick(event:MouseEvent) {
    this.collapsed = !this.collapsed;
    this.requestUpdate();
  }

  get layer():MapLayer {
    if (!this._layer) {
      if (!this.map) {
        return;
      }
    
      this._layer = this.map.layers.find((value) => {return value.id == this.layerName})
    }
    return this._layer;
  }


  onChangeVisibility() {
    this.layer.visible = this.visibilityControl.checked;
    this.notifyVisibilityChange('service', this.layerName, this.layer.visible);
  }

  onChangeOpacity() {
    let opacity:number = Number(this.opacityControl.value);
    this.layer.setOpacity(opacity / 100);
  }


  onSlotChange() {
    let tabNodes = (this.shadowRoot.querySelector('slot') as HTMLSlotElement).assignedElements({flatten:true});
    tabNodes.forEach((element, index) => {
      this.link(element);
    });

  } 


  link(element:any) {
    if (element instanceof SilverBrowserMapLayerLegend) {
      if ((element as SilverBrowserMapLayerLegend)._layerControl !== this) {
        (element as SilverBrowserMapLayerLegend)._layerControl = this;
        (element as SilverBrowserMapLayerLegend).updateLegend();
      }
    }
}


notifyLayerInfo(event:MouseEvent, layerInfo:ILegendLayerInfo) {
  let infoEvent = new CustomEvent<ILayerInfoEvent>('layerInfo',{
    detail:{
      source:event,
      layerName:layerInfo.name,
      layerInfo:layerInfo,
      extraInfo:layerInfo.extraInfo
    }});
  this.dispatchEvent(infoEvent);
}

notifyVisibilityChange(elementType: 'layer' | 'group' | 'service', name:string, visibility:boolean) { 
  let visibilityEvent = new CustomEvent<ILayerVisibilityChangeEvent>('layerVisibilityChange', {
    detail: {
      elementType: elementType,
      name:name,
      visibility:visibility
    }
  });
  this.dispatchEvent(visibilityEvent);
}

protected updateFilter() {
  this.renderRoot.querySelectorAll('sb-map-layer-legend').forEach(element => {(element as SilverBrowserMapLayerLegend).filter = this._filter});
}

renderTilesDynamic():TemplateResult {
  let layer:MapLayerTilesDynamic = this.layer as MapLayerTilesDynamic

  let onChange = (e:Event) => {
    let select:HTMLSelectElement = <any>e.target;
    layer.providerIndex = select.selectedIndex;
  }

  return html`
    <div class="legend-tile-dynamic">
      <select class="legend-tile-select" @change=${(e:Event) => {onChange(e)}}>
        ${layer.providers.map((provider,index) => html`<option ?selected=${layer.providerIndex == index} >${provider.caption}</option>`)}
      </select>
    </div>
  `
}

render():TemplateResult {
  if (!this.layer) {
    return;
  }

  let collapsed = this.collapsed;

  let canCollapse = true;
  if (this.auto && this.layer instanceof MapLayerTilesDynamic) {
    canCollapse = false;
  }

  if (!canCollapse) {
    collapsed = false;
  }


  return html`
    <div class="legend-container ${(collapsed ? 'closed' : 'opened')}">
      <div class="legend-header">
        <div class="legend-display"><input id="visibilityControl"  type="checkbox" .checked=${this.layer.visible} @input=${this.onChangeVisibility}></input></div>
        <div class="legend-caption">${this.layer.caption || this.layer.id}</div>
      </div>

      <div class="legend-header">
        <div class="legend-display"></div>
        <div class="legend-caption"><input id="opacityControl" type="range" min="1" max="100" value=${this.layer.opacity * 100} class="slider" @input=${() => this.onChangeOpacity()}></input></div>
        <div class="legend-buttons">
          ${canCollapse ? html`
            <div class=${"legend-collapse " + (this.collapsed ? "open" : "close")}
                  @click=${(event: MouseEvent) => this.handleCollapseClick(event)}>
            </div>
          ` : null}
        </div>
      </div>

      <div class="legend-detail ${classMap({haveFilter:this.haveFilter})}">
        <slot @slotchange=${() => this.onSlotChange()}></slot>
        ${this.auto && this.layer instanceof MapLayerKMS ? html`<sb-map-layer-legend ${ref(value => this.link(value))}></sb-map-layer-legend>` : null}
        ${this.auto && this.layer instanceof MapLayerTilesDynamic ? this.renderTilesDynamic(): null}
      </div>

    </div>

  `;
}

}

export interface ILayerVisibilityChangeEvent {
  elementType: string;
  name:string;
  visibility:boolean;
}

export interface ILayerInfoEvent {
  source:Event,
  layerName:string;
  layerInfo:ILegendLayerInfo,
  extraInfo:string
}